<script>
  import { createEventDispatcher } from 'svelte'

  import authStore from '../auth/store.js'

  import toastStore from './toast/store.js'

  import PageSub from './page-sub.svelte'
  import TaskEdit from './task-edit.svelte'
  import TaskListEntry from './task-list-entry.svelte'


  const PAGE_SIZE = 10

  export let queueId
  export let mode = 'current'

  let editTaskId
  const closeEdit = function () {
    editTaskId = undefined
  }
  const editTask = function (taskId) {
    editTaskId = taskId
  }
  const editTaskUpdated = function () {
    updateSelectedQueue(queueId)
    editTaskId = undefined
  }

  $: title = mode === 'old' ? 'Old Tasks' : 'Current Tasks'

  let tasks
  let paging
  let refreshInProgress = false
  let pageInProgress = false

  const updateSelectedQueue = function (queueId) {
    // Reset tasks state before trying to load tasks again. Don't want to be stuck with a
    // state mismatch between the queue and tasks if the load fails.
    tasks = undefined
    paging = undefined

    loadTasks(queueId)
  }

  $: updateSelectedQueue(queueId)

  const getTasks = function (limit, cursor_task=null) {
    const params = {
      queue_id: queueId,
      limit,
    }
    if (mode === 'old') {
      params.status = 'completed,deleted'
      params.deferred = true  // Deleted tasks can be deferred if deleted from the task list.
      params.cursor_sort = '-updated'
      if (cursor_task) {
        params.cursor_dt = cursor_task.update_dt
        params.cursor_id = cursor_task.task_id
      }
    } else {
      params.deferred = true
      params.cursor_sort = 'queued'
      if (cursor_task) {
        params.cursor_dt = cursor_task.queue_dt
        params.cursor_id = cursor_task.task_id
      }
    }

    return authStore.request({method: 'get', url: '/api/tasks', params})
      .then(response => response.data)
  }

  const loadTasks = function (resetLimit=false) {
    let limit
    if (resetLimit || tasks === undefined) {
      limit = PAGE_SIZE
    } else {
      limit = Math.max(tasks.length, PAGE_SIZE)
    }

    refreshInProgress = true
    return getTasks(limit)
      .then(data => {
        tasks = data.tasks
        paging = data.paging
      })
      .finally(() => {
        refreshInProgress = false
      })
      .then(() => {
        // See if we can auto page after the refresh.
        maybeAutoPage()
      })
      .catch(e => toastStore.error('Failed to load tasks', e))
  }

  const pageTasks = function () {
    if (!tasks || !tasks.length) {
      return Promise.reject(new Error('pageTasks() called when no tasks are loaded'))
    }
    if (!paging || !paging.more) {
      return Promise.reject(new Error('pageTasks() called when no more pages available'))
    }
    if (pageInProgress || refreshInProgress) {
      // Essentially just ignore page requests while a refresh or page is in progress
      return Promise.resolve()
    }
    pageInProgress = true
    const cursor_task = tasks[tasks.length - 1]
    return getTasks(PAGE_SIZE, cursor_task)
      .then(data => {
        // Only update tasks with the new page if the cursor task is still the last task.
        // If not, tasks has been updated by another action in the meantime, and the new page is
        // probably wrong.
        if (tasks[tasks.length - 1].task_id === cursor_task.task_id) {
          tasks = [...tasks, ...data.tasks]
          paging = data.paging
        }
      })
      .finally(() => {
        pageInProgress = false
      })
      .then(() => {
        // See if we can auto page again after the page.
        maybeAutoPage()
      })
      .catch(e => toastStore.error('Failed to load more tasks', e))
  }

  const maybeAutoPage = function () {
    // Lots of conditions to satisfy before we try and auto page...
    if (paging != undefined && paging.more && !pageInProgress && !refreshInProgress) {
      const scrollBox = document.getElementById('scrollBox')
      const scrollOffsetBottom = scrollBox.offsetTop + scrollBox.clientHeight

      const moreButton = document.getElementById('moreButton')
      // offsetTop is constant regardless of any scroll, so subtract scroll to get the actual offset
      // from the top of the offset parent.
      const moreButtonOffsetTop = moreButton.offsetTop - scrollBox.scrollTop

      const buttonVisible = scrollOffsetBottom - moreButtonOffsetTop
      // Require at least 10px to be visible.
      if (buttonVisible > 10) {
        pageTasks()
      }
    }
  }

  const dispatch = createEventDispatcher()

  const back = function () {
    dispatch('back')
  }

  const taskUpdated = function (event) {
    if (event.detail && event.detail.reload) {
      updateSelectedQueue(queueId)  // Task list has changed, so need to reload.
    } else {
      tasks = tasks  // Task has been updated in memory, so need to re-render list.
    }
  }
</script>

{#if editTaskId}
  <TaskEdit taskId={editTaskId} on:back={closeEdit} on:updated={editTaskUpdated} />
{:else}
  <PageSub {title} on:back={back}>
    {#if tasks === undefined}
      <div class="noticeWrapper">
        <div class="notice">
          Loading...
        </div>
      </div>
    {:else if !tasks.length}
      <div class="noticeWrapper">
        <div class="notice">
          <p>No {mode} tasks.</p>
        </div>
      </div>
    {:else}
      <div id="scrollBox" class="listWrapper scrollBlock" on:scroll={maybeAutoPage}>
        <div class="taskList">
          {#each tasks as task (task.task_id)}
            <TaskListEntry {task}
                           on:updated={taskUpdated}
                           on:edit="{() => editTask(task.task_id)}" />
          {/each}
          {#if paging.more}
            <button id="moreButton" on:click={pageTasks}>Load more...</button>
          {/if}
        </div>
      </div>
    {/if}
  </PageSub>
{/if}

<style>
  .listWrapper {
    display: flex;
    flex-direction: column;

    border-top: 1px solid var(--border-color);
    border-bottom: 1px solid var(--border-color);
  }
  .taskList {
    display: flex;
    flex-direction: column;

    padding: 10px;
  }
  .taskList > :global(*) {
    margin-bottom: 10px;
  }
  .taskList > :global(*:last-child) {
    margin-bottom: 0;
  }
</style>